import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  ref: "dtrs-root",
  class: "datetime-range-selector my-4",
  elevation: "4",
  color: "surface"
}
const _hoisted_2 = {
  key: 0,
  class: "single-date-section"
}
const _hoisted_3 = { class: "my-4" }
const _hoisted_4 = {
  key: 0,
  class: "multiple-dates-section"
}
const _hoisted_5 = { class: "mb-4" }
const _hoisted_6 = { class: "mb-4" }
const _hoisted_7 = { class: "mb-4" }
const _hoisted_8 = { class: "mb-4" }
const _hoisted_9 = { class: "my-4 mx-auto pa-4 dtrs-invalid-config-warning" }
const _hoisted_10 = { class: "my-4 mx-auto pa-4 dtrs-invalid-config-warning" }

import { watch, computed, ref, onMounted, useTemplateRef } from 'vue';
import DatePicker from '@vuepic/vue-datepicker';
import type { MillisecondRange } from '../types/datetime';
import DateRangePicker from './DateRangePicker.vue';
import DaysPicker from './DaysPicker.vue';
import DayPatterns from './DayPatterns.vue';
import MonthsPicker from './MonthsPicker.vue';
import SeasonPicker from './SeasonPicker.vue';
import YearsPicker from './YearsPicker.vue';
import SpecificTimesSelector from './SpecificTimesSelector.vue';
import TimelineVisualization from './TimelineVisualization.vue';
import { DAYS, MONTHS, generateTimeRanges, DEFAULT_TOLERANCE, ALL_DAY_TOLERANCE } from './date_time_range_generators';
import type { TimeRangeConfig, TimeRangeCreationMode, DayType, MonthType, TimeRangeConfigMultiple, TimeRangeConfigSingle } from './date_time_range_generators';
// === INTERFACES === 
// === PROPS ===
// Generated by Copilot
import { isRingConsecutive } from '@/utils/array_operations/cyclic';
import { filterForAllowedDates } from "./filter_ranges";

import { watchGoldenLayoutContainerSize } from '@/utils/golden_layout';


// === LIFECYCLE HOOKS ===

export default /*@__PURE__*/_defineComponent({
  __name: 'DateTimeRangeSelection',
  props: {
    currentDate: {},
    allowedDates: {},
    validTimes: {},
    validate: { type: Boolean },
    enableParceling: { type: Boolean },
    parcelSizeMs: {}
  },
  emits: ["ranges-change"],
  setup(__props: any, { emit: __emit }) {

// no unused vars
// === IMPORTS ===
const props = __props;

// === EMITS ===
const emit = __emit;



const tab = ref<'daterange' | 'monthrange'>('daterange');

// === COMPOSABLE SETUP ===
// Create refs for the current date to pass to the composable
const currentDateRef = ref(props.currentDate);

// === REFS ===
const singleDateCalendar = ref();
// === PICKER STATE ===
// Direct date objects for date pickers - no unnecessary timestamp conversion
const timeSelectionRadio = ref<TimeRangeCreationMode | 'tracked'>('tracked');
const startDateObj = ref<Date | null>(null);
const endDateObj = ref<Date | null>(null);
const singleDateObj = ref<Date | null>(currentDateRef.value);


watch(() => props.currentDate, (newDate) => {
  if (timeSelectionRadio.value === 'tracked') {
    currentDateRef.value = newDate;
  }
});



const datePickerYearRange = computed<[number, number]>(() => {
  if (!props.allowedDates || props.allowedDates.length === 0) {
    return [2023, new Date().getFullYear()];
  }
  const minYear = props.allowedDates ? props.allowedDates[0].getFullYear() : 2023;
  
  const maxYear = props.allowedDates 
    ? props.allowedDates[props.allowedDates.length - 1].getFullYear() 
    : new Date().getFullYear();
    
  return [minYear, maxYear];
});

const possibleYears = computed(() => {
  const dates = props.allowedDates ?? [2024];
  if (dates.length === 0) return [2023, new Date().getFullYear()];
  const years = dates.map(d => d.getFullYear());
  // Deduplicate and sort
  return [...new Set(years)].sort((a, b) => a - b);
});


function formatDateDisplay(date: Date | null): string {  
  return date?.toLocaleDateString() || '';
}


const currentDateString = computed((): string => {
  return currentDateRef.value.toLocaleDateString(undefined, 
    { year:'2-digit', month: 'numeric', day: 'numeric', timeZone: 'UTC' }
  );
});

const timeSelectionMode = ref<TimeRangeCreationMode>('multiple');
watch(timeSelectionRadio, (newVal) => {
  if (newVal === 'tracked') {
    timeSelectionMode.value = 'single';
  } else {
    timeSelectionMode.value = newVal;
  }
});
const selectedMonths = ref<MonthType[]>([...MONTHS]);
const selectedYears = ref<number[]>([...possibleYears.value]);
const selectedTimes = ref<string[]>([]);
const selectedDays = ref<DayType[]>([...DAYS]);
const timePlusMinus = ref<[number, number]>([...ALL_DAY_TOLERANCE]);
const allDay = computed(() => timePlusMinus.value[0] === ALL_DAY_TOLERANCE[0] && timePlusMinus.value[1] === ALL_DAY_TOLERANCE[1]);
const dayNames = DAYS as unknown as DayType[];
const monthNames = MONTHS as unknown as MonthType[];

const timeRangeStart = computed<Date | null>(() => {
  if (tab.value === 'monthrange') {
    // For month range, start from Jan 1 of the earliest selected year and month
    const year = selectedYears.value.length > 0 ? Math.min(...selectedYears.value) : new Date().getFullYear();
    const minMonth = selectedMonths.value.reduce((min, month) => {
      const monthIndex = MONTHS.indexOf(month);
      return monthIndex < min ? monthIndex : min;
    }, 11);
    const month = selectedMonths.value.length > 0 ? minMonth : 0;
    return new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));
  }
  if (!startDateObj.value) return null;
  const utc = Date.UTC(
    startDateObj.value.getUTCFullYear(), 
    startDateObj.value.getUTCMonth(),
    startDateObj.value.getDate(),
    0, 0, 0, 0
  );
  return new Date(utc);
});

const timeRangeEnd = computed<Date | null>(() => {
  if (tab.value === 'monthrange') {
    // For month range, end at Dec 31 of the latest selected year and month
    const year = selectedYears.value.length > 0 ? Math.max(...selectedYears.value) : new Date().getFullYear();
    const maxMonth = selectedMonths.value.reduce((max, month) => {
      const monthIndex = MONTHS.indexOf(month);
      return monthIndex > max ? monthIndex : max;
    }, 0);
    const month = selectedMonths.value.length > 0 ? maxMonth : 11;
    // Get last day of month
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return new Date(Date.UTC(year, month, lastDay, 23, 59, 59, 999));
  }
  if (!endDateObj.value) return null;
  const utc = Date.UTC(
    endDateObj.value.getUTCFullYear(), 
    endDateObj.value.getUTCMonth(),
    endDateObj.value.getDate(),
    23, 59, 59, 999
  );
  return new Date(utc);
});

const timeRangeSelectedMonths = computed<MonthType[] | undefined>(() => {
  if (tab.value === 'monthrange') {
    if (selectedMonths.value.length === MONTHS.length) {
      return undefined;
    }
    return selectedMonths.value;
  }
  return undefined;
});

const timeRangeSelectedYears = computed<number[] | undefined>(() => {
  if (tab.value === 'monthrange') {
    if (selectedYears.value.length === possibleYears.value.length) {
      return undefined;
    }
    return selectedYears.value;
  }
  return undefined;
});

const timeRangeConfig = computed<TimeRangeConfig>(() => {
  if (timeSelectionRadio.value === 'single' || timeSelectionRadio.value === 'tracked') {
    console.log('Single date config:');
    return {
      type: 'single',
      singleDate: timeSelectionRadio.value === 'tracked'? currentDateRef.value : singleDateObj.value,
    } as TimeRangeConfigSingle;
  } else {
    console.log('Multiple date config:');
    const patternConfig: TimeRangeConfigMultiple = {
      type: 'multiple',
      dateRange: {
        start: timeRangeStart.value ?? new Date(),
        end: timeRangeEnd.value ?? new Date(),
      },
      // years needs to send undefined for all years selected
      years: timeRangeSelectedYears.value,
      // months: (selectedMonths.value.length === 0 || allMonthsSelected.value) ? undefined : selectedMonths.value,
      // weekdays: (selectedDays.value.length === 0 || allDaysSelected.value) ? undefined : selectedDays.value,
      // times: ((selectedTimes.value.length > 0) && !allDay.value) ? selectedTimes.value : undefined,
      months: timeRangeSelectedMonths.value,
      weekdays: selectedDays.value.length === DAYS.length ? undefined : selectedDays.value,
      times: allDay.value ? undefined : selectedTimes.value,
      toleranceHours: allDay.value ? [...ALL_DAY_TOLERANCE] : [...DEFAULT_TOLERANCE],
      // Generated by Copilot - Include parceling configuration from props
      parcel: props.enableParceling ?? false,
      parcelSize: props.parcelSizeMs,
    };
    return patternConfig as TimeRangeConfig;
  }
});

const invalidConfig = computed<boolean>(() => {
  const config = timeRangeConfig.value;
  if (config.type === 'single') {
    return config.singleDate === null;
  } else {
    if (config.years !== undefined && config.years.length === 0) {
      return true;
    }
    if (config.months !== undefined && config.months.length === 0) {
      return true;
    }
    if (config.weekdays !== undefined && config.weekdays.length === 0) {
      return true;
    } 
    if (config.times !== undefined && config.times.length === 0) {
      return true;
    }
  }
  return false;
});

watch(timeRangeConfig, (newConfig) => {
  console.log('Time Range Config changed:', newConfig);
});

function asRangeOrList<T extends string>(arr: T[], order: T[]): string {
  if (arr.length === 0) return '';  
  if (arr.length <= 2) {
    return arr.map(s => s.slice(0,3)).join(', ');
  }
  const sortedArrr = arr.slice().sort((a, b) => order.indexOf(a) - order.indexOf(b));
  const isConsecutive = isRingConsecutive(sortedArrr,  order, true);
  if (isConsecutive.consecutive && sortedArrr.length > 2) {
    return `${isConsecutive.start.slice(0,3)}-${isConsecutive.end.slice(0,3)}`;
  }
  return arr.map(s => s.slice(0,3)).join(', ');
}
  

const customTimeRangeName = computed((): string => {
  
  if (timeSelectionMode.value === 'single') {
    return `${singleDateObj.value ? formatDateDisplay(singleDateObj.value) : 'No date selected'}`;
  }
    
  let dateRangeString = '';
  let monthsString = '';
  let yearsString = '';
  let daysString = '';
  let timesString = '';
  if (startDateObj.value && endDateObj.value) {
    dateRangeString = `${formatDateDisplay(startDateObj.value)} - ${formatDateDisplay(endDateObj.value)}`;
  } else {
    // we shouldn't be able to reach this, so just throw an error
    throw new Error('Start date or end date is null');
  }
  
  if (selectedMonths.value.length > 0) {
    monthsString = `Months: ${asRangeOrList(selectedMonths.value, monthNames)}`;
  }
  if (selectedYears.value.length > 0) {
    yearsString = `Years: ${selectedYears.value.join(', ')}`;
  }
  if (selectedDays.value.length > 0) {
    daysString = `${asRangeOrList(selectedDays.value, dayNames)}`;
  }
  if (selectedTimes.value.length > 0 && !allDay.value) {
    timesString = `Times: ${selectedTimes.value.join(', ')}`;
  }
  if (tab.value === 'monthrange') {
    // For monthrange tab, omit date range string
    dateRangeString = '';
  }
  if (tab.value === 'daterange') {
    // For daterange tab, omit months and years string
    monthsString = '';
    yearsString = '';
  }
  
  return [dateRangeString, monthsString, yearsString, daysString, timesString]
    .filter(s => s !== '')
    .join(' | ');
    
});

const currentTimeRanges = ref<MillisecondRange[]>([]);
const submissionTried = ref<boolean>(false);
// === METHODS ===
// Update custom range button handler
function updateCustomRange(doEmit=true) {
  let currentRanges: MillisecondRange[] = [];
  currentRanges = generateTimeRanges(timeRangeConfig.value, doEmit);
  
  // Filter ranges to only include those with allowed dates
  if (props.allowedDates && props.allowedDates.length > 0) {
    currentRanges = filterForAllowedDates(currentRanges, props.allowedDates);
  }
  
  currentTimeRanges.value = currentRanges;
  submissionTried.value = doEmit;
  if (doEmit) {
    emit('ranges-change', currentRanges, timeSelectionMode.value, customTimeRangeName.value, timeRangeConfig.value);
  }
}

watch(timeRangeConfig, () => {
  if (props.validTimes) updateCustomRange(false);
}, { immediate: true, deep: true });

function handleSingleDateChange(value: Date) {
  if (value && value.getTime() !== singleDateObj.value?.getTime()) {
    singleDateObj.value = value;
    singleDateCalendar.value?.closeMenu();
  }
}

const dtrsRoot = useTemplateRef('dtrs-root');
const glContainerSize = ref<{width: number; height: number}>({width: 0, height: 0});
onMounted(() => {
  if (dtrsRoot.value) {
    // console.log(dtrsRoot.value);
    watchGoldenLayoutContainerSize(dtrsRoot.value as HTMLElement, (size) => {
      glContainerSize.value = size;
    });
  }
  // Initialize default dates
  if (props.allowedDates && props.allowedDates.length > 0) {
    const end = new Date(props.allowedDates[props.allowedDates.length - 1]);
    const start = new Date(end);
    // set back by 1 week
    start.setDate(end.getDate() - 6);
    startDateObj.value = start;
    endDateObj.value = end;
  }
});
// watch(glContainerSize, (newSize) => {
//   console.log('DTRS Golden Layout container size changed:', newSize);
// });
// === WATCHERS ===
// Watch for prop changes and update refs
watch(() => props.currentDate, (newDate) => {
  currentDateRef.value = newDate;
});



// Ranges are now only emitted when the Update Custom Range button is clicked

return (_ctx: any,_cache: any) => {
  const _component_v_radio = _resolveComponent("v-radio")!
  const _component_v_expand_transition = _resolveComponent("v-expand-transition")!
  const _component_v_radio_group = _resolveComponent("v-radio-group")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_btn_toggle = _resolveComponent("v-btn-toggle")!
  const _component_v_tabs_window_item = _resolveComponent("v-tabs-window-item")!
  const _component_v_tabs_window = _resolveComponent("v-tabs-window")!
  const _component_v_expansion_panel_text = _resolveComponent("v-expansion-panel-text")!
  const _component_v_expansion_panel = _resolveComponent("v-expansion-panel")!
  const _component_v_expansion_panels = _resolveComponent("v-expansion-panels")!
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[20] || (_cache[20] = _createElementVNode("h4", null, "Select Date(s)", -1)),
    _createVNode(_component_v_radio_group, {
      modelValue: timeSelectionRadio.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((timeSelectionRadio).value = $event)),
      direction: "horizontal",
      density: "comfortable"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_radio, {
          label: `Date Shown On Map: ${currentDateString.value}`,
          value: "tracked"
        }, null, 8, ["label"]),
        _createVNode(_component_v_radio, {
          label: "Single Day",
          value: "single"
        }, {
          label: _withCtx(() => [
            _cache[13] || (_cache[13] = _createElementVNode("span", {
              class: "mr-2",
              style: {"text-wrap":"nowrap"}
            }, "Single Day", -1)),
            (timeSelectionRadio.value === 'single' && glContainerSize.value.width > 450)
              ? (_openBlock(), _createBlock(_unref(DatePicker), {
                  key: 0,
                  class: "mx-2 ddrp__date-picker",
                  ref_key: "singleDateCalendar",
                  ref: singleDateCalendar,
                  "model-value": singleDateObj.value,
                  onInternalModelChange: handleSingleDateChange,
                  "allowed-dates": __props.allowedDates,
                  format: formatDateDisplay,
                  "preview-format": formatDateDisplay,
                  clearable: false,
                  "text-input": "",
                  teleport: true,
                  dark: "",
                  "year-range": datePickerYearRange.value,
                  "week-start": 0,
                  "six-weeks": "",
                  "enable-time-picker": false
                }, null, 8, ["model-value", "allowed-dates", "year-range"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        _createVNode(_component_v_expand_transition, { title: "Select a Single Date" }, {
          default: _withCtx(() => [
            (timeSelectionRadio.value === 'single' && glContainerSize.value.width <= 450)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    _createVNode(_unref(DatePicker), {
                      class: "mx-2 ddrp__date-picker",
                      ref_key: "singleDateCalendar",
                      ref: singleDateCalendar,
                      "model-value": singleDateObj.value,
                      onInternalModelChange: handleSingleDateChange,
                      "allowed-dates": __props.allowedDates,
                      format: formatDateDisplay,
                      "preview-format": formatDateDisplay,
                      clearable: false,
                      "text-input": "",
                      teleport: true,
                      dark: "",
                      "year-range": datePickerYearRange.value,
                      "week-start": 0,
                      "six-weeks": "",
                      "enable-time-picker": false
                    }, null, 8, ["model-value", "allowed-dates", "year-range"])
                  ])
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        _createVNode(_component_v_radio, {
          label: "Multiple Days",
          value: "multiple"
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_expand_transition, { title: "Select Multiple Dates" }, {
      default: _withCtx(() => [
        (timeSelectionRadio.value === 'multiple')
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", null, [
                _createVNode(_component_v_btn_toggle, {
                  modelValue: tab.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((tab).value = $event)),
                  mandatory: "",
                  color: "var(--info-background)",
                  density: "compact"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_btn, {
                      class: "daterange-btn",
                      variant: tab.value == 'daterange' ? 'flat' : 'tonal',
                      value: "daterange",
                      size: "small"
                    }, {
                      default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                        _createTextVNode(" Custom Range ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["variant"]),
                    _createVNode(_component_v_btn, {
                      class: "monthrange-btn",
                      variant: tab.value == 'monthrange' ? 'flat' : 'tonal',
                      value: "monthrange",
                      size: "small"
                    }, {
                      default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                        _createTextVNode(" Quick Select ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["variant"])
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _createVNode(_component_v_tabs_window, {
                modelValue: tab.value,
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((tab).value = $event)),
                crossfade: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_tabs_window_item, {
                    "selected-class": "dtrs-tab-window-selected",
                    class: "dtrs-tab-window",
                    value: "daterange"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(DateRangePicker, {
                        "start-date": startDateObj.value,
                        "onUpdate:startDate": _cache[2] || (_cache[2] = ($event: any) => ((startDateObj).value = $event)),
                        "end-date": endDateObj.value,
                        "onUpdate:endDate": _cache[3] || (_cache[3] = ($event: any) => ((endDateObj).value = $event)),
                        "allowed-dates": __props.allowedDates,
                        "format-function": formatDateDisplay,
                        clearable: false,
                        "text-input": true,
                        teleport: true,
                        dark: true,
                        "year-range": datePickerYearRange.value
                      }, null, 8, ["start-date", "end-date", "allowed-dates", "year-range"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_tabs_window_item, {
                    "selected-class": "dtrs-tab-window-selected",
                    class: "dtrs-tab-window",
                    value: "monthrange"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_5, [
                        _createVNode(MonthsPicker, {
                          modelValue: selectedMonths.value,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedMonths).value = $event)),
                          "show-error-for-empty": ""
                        }, null, 8, ["modelValue"]),
                        _createVNode(SeasonPicker, {
                          class: "mt-4",
                          modelValue: selectedMonths.value,
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((selectedMonths).value = $event))
                        }, null, 8, ["modelValue"])
                      ]),
                      _createElementVNode("div", _hoisted_6, [
                        _createVNode(YearsPicker, {
                          modelValue: selectedYears.value,
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((selectedYears).value = $event)),
                          "possible-years": possibleYears.value,
                          "show-error-for-empty": ""
                        }, null, 8, ["modelValue", "possible-years"])
                      ])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"]),
              _createVNode(_component_v_expansion_panels, {
                class: "mb-4",
                variant: "default",
                focusable: "",
                multiple: false,
                color: "var(--info-background)"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_expansion_panel, {
                    value: "pattern",
                    title: "Filter Days / Times",
                    "bg-color": "surface"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_expansion_panel_text, { class: "pattern-section" }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_7, [
                            _createVNode(DaysPicker, {
                              modelValue: selectedDays.value,
                              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((selectedDays).value = $event)),
                              "show-error-for-empty": ""
                            }, null, 8, ["modelValue"])
                          ]),
                          _createVNode(DayPatterns, {
                            modelValue: selectedDays.value,
                            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((selectedDays).value = $event))
                          }, null, 8, ["modelValue"]),
                          _createElementVNode("div", _hoisted_8, [
                            _createVNode(SpecificTimesSelector, {
                              modelValue: selectedTimes.value,
                              "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((selectedTimes).value = $event)),
                              timePlusMinus: timePlusMinus.value,
                              "onUpdate:timePlusMinus": _cache[11] || (_cache[11] = ($event: any) => ((timePlusMinus).value = $event))
                            }, null, 8, ["modelValue", "timePlusMinus"])
                          ])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_component_v_btn, {
      color: "primary",
      disabled: invalidConfig.value,
      variant: "elevated",
      class: "mb-4 mx-auto d-block",
      onClick: updateCustomRange
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_icon, { class: "mr-2" }, {
          default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
            _createTextVNode("mdi-update", -1)
          ]))]),
          _: 1
        }),
        _cache[17] || (_cache[17] = _createTextVNode(" Confirm Selection ", -1))
      ]),
      _: 1
    }, 8, ["disabled"]),
    _withDirectives(_createElementVNode("div", _hoisted_9, " The current time range selections are invalid and result in no possible times. Please adjust your selections. ", 512), [
      [_vShow, invalidConfig.value]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_10, " There is no TEMPO data available for the time range selected. Please adjust your selections. ", 512), [
      [_vShow, submissionTried.value && currentTimeRanges.value.length===0]
    ]),
    (__props.validate)
      ? (_openBlock(), _createBlock(_component_v_btn, {
          key: 0,
          color: "info",
          variant: "outlined",
          class: "mb-4 mx-4",
          onClick: _cache[12] || (_cache[12] = () =>updateCustomRange(false))
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_icon, { class: "mr-2" }, {
              default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                _createTextVNode("mdi-help-circle", -1)
              ]))]),
              _: 1
            }),
            _cache[19] || (_cache[19] = _createTextVNode(" Validate Time Range ", -1))
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (__props.validate)
      ? (_openBlock(), _createBlock(TimelineVisualization, {
          key: 1,
          ranges: currentTimeRanges.value,
          config: timeRangeConfig.value,
          "allowed-dates": __props.allowedDates
        }, null, 8, ["ranges", "config", "allowed-dates"]))
      : _createCommentVNode("", true)
  ], 512))
}
}

})